import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';
import debouce from 'lodash/debounce';

import updateSuggestions from '../actions/action-auto-complete';
import SpeechRecognition from './SpeechRecognition';

import { showTopReason, getTopSearchReason, hideTopReason } from '../../home/actions/actions-top-contact-reason';
import {
  startRecording,
  endRecording,
  speechRecognitionRequest,
  resetSpeechQuery,
} from '../actions/action-speech-recognition';
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { trackLink } from '../../lib/analytics-helper';
import getHistory from '../../lib/HistoryHelper';
import { ARTICLE_CONTENT } from '../constants/PropTypesShapes';
import { getBNATitle } from '../../lib/messageHelper';
import { ECM_KEY_DOCID, ECM_DOC_ID_DEFAULT_VALUE } from '../constants/ecmFPTITagsKeys';

const searchPlaceHolder = {
  largeScreen: i18n.message('smarthelp/heroSection.searchbox.placeholderText.largeScreen'),
  smallScreen: i18n.message('smarthelp/heroSection.searchbox.placeholderText.smallScreen'),
  hcrPlaceHolder: i18n.message('smarthelp/heroSection.searchbox.placeholderText.hcrPlaceHolder'),
  speechHolder: i18n.message('smarthelp/heroSection.searchbox.speechRecognition.placeholder'),
};

const viewSearchResult = i18n.message('smarthelp/heroSection.searchbox.viewSearchResult');
const defaultTopReason = i18n.message('smarthelp/heroSection.searchbox.defaultTopReason');

const label = {
  search: i18n.message('smarthelp/heroSection.searchbox.searchBtnText'),
};

const cobrand = Page.getCountry().toLowerCase();
const defaultSearchTreatment = 'Trmt_GOPS_Helpcenter_PCC_Default_Search_1';

class SearchBox extends Component {
  // Autosuggest propTypes expects this function to be present
  static onSuggestionsClearRequested() {}

  static getSearchIcon() {
    return 'vx_icon vx_icon-small vx_icon-magnifying-glass';
  }

  static getSanitizedValue(value) {
    return value ? value.trim() : '';
  }

  static getPlaceHolderText() {
    if (Page.isMobileLayout()) {
      return searchPlaceHolder.smallScreen;
    }
    return searchPlaceHolder.hcrPlaceHolder;
  }

  // render HCR suggestions followed by View all Results
  static renderSuggestion(faq) {
    if (faq.title === viewSearchResult) {
      return <div className="viewSearchResult">{viewSearchResult}</div>;
    }
    return <div className="result-item">{faq.title}</div>;
  }

  static addViewResultIntoSuggestion(suggestions) {
    const submitSearchResult = {
      title: viewSearchResult,
    };
    if (suggestions.length > 0 && suggestions.every((suggestion) => suggestion.title !== viewSearchResult)) {
      suggestions.push(submitSearchResult);
    }
  }

  constructor(props) {
    super(props);

    this.state = {
      inputText: '',
      inputFocus: false,
      enterHit: false,
    };

    this.MIN_LENGTH = 1; // min characters required for search
    this.suggestSelected = false; // preventing auto-complete request after selected one of the suggestions

    this.submitForm = this.submitForm.bind(this);
    this.debouncedUpdateSuggestions = debouce(this.props.updateSuggestions, 300);

    // action-auto-complete functions
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);

    // view all result getting the state inputText
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.handleClearKeyPress = this.handleClearKeyPress.bind(this);
    this.renderClearButton = this.renderClearButton.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.redirectSearchPage = this.redirectSearchPage.bind(this);
    this.startSpeechRecoginition = this.startSpeechRecoginition.bind(this);
    this.endSpeechRecoginition = this.endSpeechRecoginition.bind(this);
    this.speechRecoginitionErrorHandler = this.speechRecoginitionErrorHandler.bind(this);
    this.renderTopReason = this.renderTopReason.bind(this);
    this.getSuggestionHeader = this.getSuggestionHeader.bind(this);
  }

  componentDidMount() {
    // API call
    this.props.getTopSearchReason();
  }

  componentWillReceiveProps(props) {
    if (props.q !== this.props.q) {
      this.setState({
        inputText: props.q,
      });
    }
  }

  handleClearKeyPress(event) {
    if (event.key === 'Enter') {
      this.clearSearch();
    }
  }

  onChange(event, { newValue }) {
    if (event.type === 'change') {
      this.setState({
        inputText: newValue,
        enterHit: false,
      });
    }

    if (newValue.length > 0 || this.props.q) {
      // Default contact reasons handler
      this.props.hideTopReason();
    }
  }

  onFocus() {
    this.setState({
      inputFocus: true,
    });

    // Default contact reasons handler
    if (this.isEnableDefaultReason()) {
      this.props.showTopReason();
    }
  }

  onBlur() {
    this.setState({
      inputFocus: false,
    });
    // Default contact reasons handler
    if (this.isEnableDefaultReason() || this.props.q) {
      this.props.hideTopReason();
    }
  }

  onKeyUp(event) {
    if (event.key === 'Enter') {
      this.setState({
        enterHit: true,
      });
    }
  }

  onSuggestionsFetchRequested({ value }) {
    // HACK: Cancel fetching the auto-complete event after selecting one of the suggestions
    if (this.suggestSelected) {
      this.suggestSelected = false;
      return;
    }

    const sanitizedValue = Page.getSanitizedValue(value);
    const sanitizedInputText = Page.getSanitizedValue(this.state.inputText);
    if (sanitizedInputText !== sanitizedValue && sanitizedValue.length > this.MIN_LENGTH) {
      this.debouncedUpdateSuggestions(sanitizedValue);
    }
  }

  onSuggestionSelected(event, { suggestion }) {
    event.preventDefault();
    event.stopPropagation();
    this.suggestSelected = true; // Mark one suggestion be selected
    if (suggestion.title === viewSearchResult) {
      trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search-term', srch: `${this.state.inputText}` });
      this.goToSearchResult();
    } else {
      const title = Page.getFaqUrlFromTitle(suggestion.title);
      const faq = suggestion.docid ? suggestion.docid.toLowerCase() : [];
      // get the ECM API tags from article data
      const fptiDocId = suggestion[ECM_KEY_DOCID] || ECM_DOC_ID_DEFAULT_VALUE;

      if (faq.length > 0) {
        // Top contact reasons will be displayed when this.props.isDisplayTopReason is true
        if (this.props.isDisplayTopReason) {
          trackLink({
            id: `${this.props.FPTIId}`,
            link: `faq-autoquicklink|${faq}`,
            hpid: faq,
            help_doc_id: fptiDocId,
          });
        } else {
          trackLink({
            id: `${this.props.FPTIId}`,
            link: `smarthelp-selected-suggested-keyword-km-auto-complete|${this.state.inputText}|${suggestion.docid}|${title}|${suggestion.rank}|total-${this.props.searchResults.length}`,
            hpid: faq,
            help_doc_id: fptiDocId,
          });
        }
        window.location = `/${cobrand}/smarthelp/article/${title}-${faq}?app=searchAutoComplete`;
      }
    }
  }

  getSuggestionValue(faq) {
    if (faq.title === viewSearchResult) {
      return this.state.inputText;
    }
    return faq.title;
  }

  // formate contact top reason data which is same with spotlight search
  getDefaultSeachReason() {
    if (!this.props.isDisplayTopReason) {
      return [];
    }

    if (this.isEnablePCCDefaultArticles()) {
      return this.props.defaultArticles.slice(0, 5);
    }
    return this.props.defaultTopReason;
  }

  // Get the default auto-complete header as the first option
  getSuggestionHeader() {
    if (this.isEnablePCCDefaultArticles()) {
      return getBNATitle();
    }

    return defaultTopReason;
  }

  // Adding the suggestion header in the suggestions array
  addDefaultReason(suggestions) {
    const defaultReason = {
      title: this.getSuggestionHeader(),
    };

    const tempArray = [...suggestions];
    if (suggestions.length && suggestions.every((suggestion) => suggestion.title !== defaultTopReason)) {
      tempArray.unshift(defaultReason);
      return tempArray;
    }
    return tempArray;
  }

  isEnablePCCDefaultArticles() {
    return (
      Array.isArray(this.props.treatments) &&
      this.props.treatments.indexOf(defaultSearchTreatment) >= 0 &&
      Array.isArray(this.props.defaultArticles) &&
      this.props.defaultArticles.length > 0
    );
  }

  isEnableDefaultReason() {
    return (
      !this.hasMinimimSearchCharacters() &&
      Array.isArray(this.props.defaultTopReason) &&
      this.props.defaultTopReason.length > 0
    );
  }

  hasMinimimSearchCharacters() {
    const sanitizedInputText = SearchBox.getSanitizedValue(this.state.inputText);
    return sanitizedInputText.length >= this.MIN_LENGTH;
  }

  goToSearchResult() {
    const q = Page.getSanitizedValue(this.state.inputText);
    const { channel } = this.props.queryParams;
    const channelQueryParam = channel ? `&channel=${channel}` : '';
    const searchUrl = `search?q=${q}${channelQueryParam}`;
    this.props.resetSpeechQuery();

    // Redirect to Search Page, if its from Channel Steering.
    if (Page.getPageName() === 'contact-us') {
      const searchBaseUrl = Page.getBaseURLWithExperience();
      window.location = `/${searchBaseUrl}/${searchUrl}`;
    } else {
      const history = getHistory();
      history.push(`/${searchUrl}`);
    }
  }

  submitForm(event) {
    event.preventDefault();
    if (this.state.inputText) {
      // Empty search text will not fire FPTI request
      trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search-term', srch: `${this.state.inputText}` });
    }

    const searchText = Page.getSanitizedValue(this.state.inputText);
    if (searchText.length < this.MIN_LENGTH) {
      return;
    }

    this.goToSearchResult();
  }

  clearSearch() {
    // FPTI event for clearing search
    trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-hcr-clear-search' });
    this.props.hideTopReason();
    this.setState({
      inputText: '',
    });
  }

  // Start event hander for SpeechRecognition
  startSpeechRecoginition() {
    trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search|speech-to-text|start' });
    this.props.startRecording();
  }

  endSpeechRecoginition() {
    this.props.endRecording();
  }

  speechRecoginitionErrorHandler(error) {
    trackLink({ id: `${this.props.FPTIId}`, link: `smarthelp-search|speech-to-text|error|${error}` });
  }

  // Onresult event handler for the SpeechRecognition component
  redirectSearchPage(input) {
    const query = Page.getSanitizedValue(input);
    this.props.speechRecognitionRequest(query);
    const { channel } = this.props.queryParams;
    const channelQueryParam = channel ? `&channel=${channel}` : '';

    trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search|speech-to-text|search', srch: `${input}|speech` });
    const history = getHistory();
    history.push(`/search?q=${query}${channelQueryParam}`);
  }

  renderTopReason(faq) {
    if (faq.title === this.getSuggestionHeader()) {
      return <div className="defaultTopReason">{this.getSuggestionHeader()}</div>;
    }
    return <div className="result-item">{faq.title}</div>;
  }

  // Rendering SpeechRecognition when ELMO is enable and browser type is Chrome
  renderSpeechRecoginition() {
    if (!Page.isShowSpeechRecognition()) {
      return null;
    }

    return (
      <SpeechRecognition
        onStart={this.startSpeechRecoginition}
        onEnd={this.endSpeechRecoginition}
        onResult={this.redirectSearchPage}
        onError={this.speechRecoginitionErrorHandler}
      />
    );
  }

  renderClearButton() {
    if (!this.state.inputText.trim()) return null;

    return (
      <div // eslint-disable-line jsx-a11y/no-static-element-interactions
        className="cw_icon cw_icon-small cw_icon-close-small clear-btn"
        tabIndex="0"
        role="button"
        aria-label="clear search"
        onKeyPress={this.handleClearKeyPress}
        onClick={this.clearSearch}
      />
    );
  }

  renderAutoSuggetion({ suggestions, renderInput }) {
    const attr = {
      suggestions,
      onSuggestionsFetchRequested: this.onSuggestionsFetchRequested,
      onSuggestionsClearRequested: SearchBox.onSuggestionsClearRequested,
      onSuggestionSelected: this.onSuggestionSelected,
      getSuggestionValue: this.getSuggestionValue,
      renderSuggestion: SearchBox.renderSuggestion,
      focusInputOnSuggestionClick: false,
      inputProps: this.inputProps,
      renderInputComponent: renderInput,
      alwaysRenderSuggestions: true,
    };

    if (this.props.isDisplayTopReason) {
      attr.renderSuggestion = this.renderTopReason;
    }
    return <Autosuggest {...attr} />;
  }

  render() {
    this.inputProps = {
      placeholder: SearchBox.getPlaceHolderText(),
      value: this.state.inputText || '',
      onChange: this.onChange,
      onBlur: this.onBlur,
      onFocus: this.onFocus,
      onKeyUp: this.onKeyUp,
      className: 'search-input',
      maxLength: 250,
      id: 'help-input-text',
      'aria-label': 'search-input',
    };

    // Update Search box placeholder when recording users' speech
    if (this.props.isSpeechRecording) {
      this.inputProps.placeholder = searchPlaceHolder.speechHolder;
    }

    const containerStyle = classNames({
      'input-text-container': true,
      'input-text__speech-recognition-enable': Page.isShowSpeechRecognition(),
    });

    const renderInput = (inputProps) => (
      <div className={containerStyle}>
        <label htmlFor="help-input-text" className="label-help-input-text sr-only">
          {SearchBox.getPlaceHolderText()}
        </label>
        <input {...inputProps} />
        {this.renderClearButton()}
        {this.renderSpeechRecoginition()}
        <button type="submit" className="search-btn" alt="Search" value={label.search} onClick={this.submitForm}>
          <span className={SearchBox.getSearchIcon()} />
          <span className="sr-only">{label.search}</span>
        </button>
      </div>
    );
    let suggestions = [];
    // Adding suggestions when inputFocus is true
    if (this.hasMinimimSearchCharacters() && this.state.inputFocus && !this.state.enterHit) {
      suggestions = this.props.searchResults;
      SearchBox.addViewResultIntoSuggestion(suggestions);
    } else {
      suggestions = this.getDefaultSeachReason();
      suggestions = this.addDefaultReason(suggestions);
    }

    return (
      <div className="search-box">
        <form role="search">
          <div className="search-box-contents">{this.renderAutoSuggetion({ suggestions, renderInput })}</div>
        </form>
      </div>
    );
  }
}

const suggestionItem = PropTypes.shape({
  title: PropTypes.string,
  docid: PropTypes.string,
});

SearchBox.propTypes = {
  isSpeechRecording: PropTypes.bool,
  q: PropTypes.string.isRequired,
  queryParams: PropTypes.shape({
    channel: PropTypes.string,
  }),
  isDisplayTopReason: PropTypes.bool,
  updateSuggestions: PropTypes.func.isRequired,
  getTopSearchReason: PropTypes.func.isRequired,
  hideTopReason: PropTypes.func.isRequired,
  showTopReason: PropTypes.func.isRequired,
  startRecording: PropTypes.func.isRequired,
  endRecording: PropTypes.func.isRequired,
  resetSpeechQuery: PropTypes.func.isRequired,
  speechRecognitionRequest: PropTypes.func.isRequired,
  defaultTopReason: PropTypes.arrayOf(suggestionItem).isRequired,
  searchResults: PropTypes.arrayOf(suggestionItem).isRequired,
  FPTIId: PropTypes.string,
  treatments: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultArticles: PropTypes.arrayOf(PropTypes.shape(ARTICLE_CONTENT)).isRequired,
};

SearchBox.defaultProps = {
  q: '',
  isSpeechRecording: false,
  queryParams: {},
  FPTIId: 'homePage',
  isDisplayTopReason: null,
  treatments: [],
  defaultArticles: [],
};

function mapStateToProps(state) {
  return {
    isSpeechRecording: state.speechRecognition.isRecording,
    q: state.searchResult.q,
    queryParams: {
      channel: state.query.params.channel,
    },
    searchResults: state.autoComplete.searchResults,
    isDisplayTopReason: state.contactReason.isDisplayTopReason,
    defaultTopReason: state.contactReason.defaultTopReason,
    treatments: state.app.elmoTreatments,
    defaultArticles: state.defaultPCCArticles.defaultArticles, // default PCC articles
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      updateSuggestions,
      showTopReason,
      getTopSearchReason,
      hideTopReason,
      startRecording,
      endRecording,
      speechRecognitionRequest,
      resetSpeechQuery,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchBox);
